import React from "react";
import LegalShell, { LegalSection, LegalList } from "@/components/LegalShell";

// ============================================================================
// /refund-policy — public legal page (no login required).
// ============================================================================
const CONTACT_EMAIL = "rigsentinel@gmail.com";

export default function RefundPolicy() {
  return (
    <LegalShell
      title="Refund Policy"
      subtitle="This Refund Policy explains when and how you may request a refund for a paid RigSentinel subscription. It is part of, and must be read together with, our Terms of Service."
      lastUpdated="February 2026"
      testId="page-refund-policy"
    >
      <LegalSection id="overview" title="1. Overview">
        <p>
          RigSentinel offers paid subscription plans (Field Pro and
          Professional) billed monthly or annually. Subscriptions renew
          automatically until cancelled. This policy describes refund
          eligibility, the request process, and how cancellations work.
        </p>
      </LegalSection>

      <LegalSection id="eligibility" title="2. Refund Eligibility">
        <p>
          We offer a <strong>7-day refund window</strong> on the initial
          purchase of any paid plan, provided that the account has not made
          substantial use of paid-only features (defined as more than a
          reasonable trial volume of AI polishing requests, premium report
          generations, or premium PDF exports). To request a refund within
          the window, contact us at{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="underline"
            style={{ color: "var(--rs-amber)" }}
            data-testid="refund-contact-email"
          >
            {CONTACT_EMAIL}
          </a>{" "}
          with your account email and order details.
        </p>
        <p>
          The following are <strong>not eligible</strong> for refund:
        </p>
        <LegalList
          items={[
            "Renewal charges on a previously active subscription (cancel before the renewal date to avoid being billed for the next period).",
            "Requests made more than 7 days after the initial purchase.",
            "Accounts that have substantially used paid-only features beyond a reasonable trial volume.",
            "Accounts terminated or suspended for breach of the Terms of Service.",
            "Partial months / partial years on an active subscription that you choose to cancel mid-period.",
          ]}
        />
      </LegalSection>

      <LegalSection id="how-to-request" title="3. How to Request a Refund">
        <p>To request a refund, email us with the following:</p>
        <LegalList
          items={[
            "The email address on your RigSentinel account.",
            "The date of purchase and approximate amount.",
            "A short note about your reason for the refund (optional but helpful).",
          ]}
        />
        <p>
          We aim to acknowledge refund requests within{" "}
          <strong>2 business days</strong> and to issue approved refunds
          within <strong>5–10 business days</strong>. Refunds are returned to
          the original payment method via our payment processor, Stripe.
          Bank settlement timing is controlled by your card issuer.
        </p>
      </LegalSection>

      <LegalSection id="cancellation" title="4. Cancellation of Recurring Subscription">
        <p>
          You may cancel a recurring monthly or annual subscription at any
          time from your account ("Manage plan"). Cancellation stops future
          renewals; you retain access to paid features through the end of the
          current paid period.
        </p>
        <p>
          Cancellation alone does not generate a refund for the current paid
          period. Refunds for the current paid period are governed by the
          eligibility rules in Section 2.
        </p>
      </LegalSection>

      <LegalSection id="failed-payments" title="5. Failed Payments and Reversals">
        <p>
          If a renewal payment fails, we may retry the charge a small number
          of times. If the charge cannot be completed, your account will be
          downgraded or its access to paid features will be suspended until
          payment is restored. Chargebacks initiated without first contacting
          us may result in immediate account suspension.
        </p>
      </LegalSection>

      <LegalSection id="discretion" title="6. Exceptional Circumstances">
        <p>
          We may, at our discretion, grant refunds outside of the standard
          eligibility window for exceptional reasons (e.g., proven duplicate
          charge, prolonged service outage). Such discretionary refunds are
          handled case by case and do not create a general right to a refund.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="7. Contact">
        <p>
          All refund requests and billing questions:{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="underline"
            style={{ color: "var(--rs-amber)" }}
            data-testid="refund-contact-email-bottom"
          >
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </LegalSection>
    </LegalShell>
  );
}
